import React, { useEffect, useMemo, useState } from "react";

const DEFAULT_API_BASE = process.env.REACT_APP_API_BASE || "https://mystockpicks.onrender.com";

function Toggle({ value, onChange, disabled }) {
  return (
    <button disabled={disabled} onClick={() => onChange(!value)}
      className={`px-3 py-2 rounded-lg border text-sm ${value ? "bg-green-900/30 border-green-700" : "bg-gray-800 border-gray-700"}`}>
      {value ? "ON" : "OFF"}
    </button>
  );
}
function Row({ label, children }) {
  return <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-800"><div className="text-sm text-gray-300">{label}</div><div>{children}</div></div>;
}
function Pill({ children }) {
  return <span className="px-3 py-1 rounded-full text-xs bg-gray-800 border border-gray-700">{children}</span>;
}

export default function StockIgnitionBotPanel({ apiBase = DEFAULT_API_BASE, token = "" }) {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }), [token]);

  async function call(path, options = {}) {
    const res = await fetch(`${apiBase}${path}`, { ...options, headers: { ...headers, ...(options.headers || {}) } });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.ok === false) throw new Error(data?.error || data?.reason || `Request failed (${res.status})`);
    return data;
  }

  async function refresh() {
    try {
      setError("");
      const [s, st, lg] = await Promise.all([
        call("/api/stock-ignition-bot/settings"),
        call("/api/stock-ignition-bot/status"),
        call("/api/stock-ignition-bot/logs?limit=120"),
      ]);
      setSettings(s.settings || {});
      setStatus(st || {});
      setLogs(lg.logs || []);
      setCandidates(st.top_candidates || []);
    } catch (e) { setError(e.message || String(e)); }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [apiBase, token]);

  async function patch(payload) {
    setBusy(true);
    try {
      const out = await call("/api/stock-ignition-bot/settings", { method: "POST", body: JSON.stringify(payload) });
      setSettings(out.settings || {});
      await refresh();
    } catch (e) { setError(e.message || String(e)); } finally { setBusy(false); }
  }
  async function scan() {
    setBusy(true);
    try {
      const out = await call("/api/stock-ignition-bot/scan", { method: "POST", body: JSON.stringify({ limit: 10, force: true }) });
      setCandidates(out.candidates || []);
      await refresh();
    } catch (e) { setError(e.message || String(e)); } finally { setBusy(false); }
  }
  async function tick(execute = false) {
    setBusy(true);
    try {
      await call("/api/stock-ignition-bot/run-tick", { method: "POST", body: JSON.stringify({ execute, force_scan: true }) });
      await refresh();
    } catch (e) { setError(e.message || String(e)); } finally { setBusy(false); }
  }

  if (!settings) return <div className="text-sm text-gray-400">Loading Stock Ignition Bot…</div>;
  const open = status?.open_trades || [];

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-orange-800 bg-orange-950/20 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-lg font-semibold">Stock Ignition Bot</div>
            <div className="text-xs text-gray-300 mt-1 max-w-3xl">
              Hunts quiet-base → volume ignition → fresh breakout/breakdown → first-retest setups. Bullish ignition buys a CALL; bearish ignition buys a PUT through Tradier.
            </div>
          </div>
          <div className="flex gap-2 flex-wrap"><Pill>{settings.engine_version || "v19.12.0"}</Pill><Pill>{settings.paper_mode ? "PAPER" : "LIVE"}</Pill><Pill>{open.length}/{settings.max_positions || 2} open</Pill></div>
        </div>
      </div>

      {error ? <div className="rounded-lg border border-red-800 bg-red-950/30 p-3 text-sm text-red-300">{error}</div> : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
          <Row label="Enabled"><Toggle disabled={busy} value={Boolean(settings.enabled)} onChange={(v) => patch({ enabled: v })} /></Row>
          <Row label="Paper Mode"><Toggle disabled={busy} value={Boolean(settings.paper_mode)} onChange={(v) => patch({ paper_mode: v })} /></Row>
          <Row label="Scan NYSE + Nasdaq"><Toggle disabled={busy} value={Boolean(settings.scan_all)} onChange={(v) => patch({ scan_all: v })} /></Row>
          <Row label="Minimum Score"><input disabled={busy} type="number" min="72" max="99" value={settings.min_score ?? 84} onChange={(e) => patch({ min_score: Number(e.target.value) })} className="w-24 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-right" /></Row>
          <Row label="Minimum Relative Volume"><input disabled={busy} type="number" min="1.1" max="10" step="0.1" value={settings.min_volume_ratio ?? 2} onChange={(e) => patch({ min_volume_ratio: Number(e.target.value) })} className="w-24 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-right" /></Row>
          <Row label="Max Positions"><input disabled={busy} type="number" min="1" max="4" value={settings.max_positions ?? 2} onChange={(e) => patch({ max_positions: Number(e.target.value) })} className="w-24 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-right" /></Row>
        </div>
        <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
          <Row label="Option DTE"><span className="text-sm">{settings.min_dte ?? 7}–{settings.max_dte ?? 30} days</span></Row>
          <Row label="Preferred DTE"><span className="text-sm">{settings.preferred_dte ?? 14} days</span></Row>
          <Row label="Min Option Volume"><span className="text-sm">{settings.min_volume ?? 50}</span></Row>
          <Row label="Min Open Interest"><span className="text-sm">{settings.min_open_interest ?? 200}</span></Row>
          <Row label="Max Spread"><span className="text-sm">{(Number(settings.max_spread_pct ?? 0.06) * 100).toFixed(1)}%</span></Row>
          <Row label="Delta Target"><span className="text-sm">{Number(settings.target_delta_min ?? 0.4).toFixed(2)}–{Number(settings.target_delta_max ?? 0.65).toFixed(2)}</span></Row>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button disabled={busy} onClick={scan} className="px-4 py-2 rounded-lg bg-blue-900/30 border border-blue-700 text-sm">Scan Now</button>
        <button disabled={busy} onClick={() => tick(false)} className="px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm">Dry Run Tick</button>
        <button disabled={busy} onClick={() => tick(true)} className="px-4 py-2 rounded-lg bg-orange-900/30 border border-orange-700 text-sm">Execute Tick</button>
        <button disabled={busy} onClick={refresh} className="px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm">Refresh</button>
      </div>

      <div className="bg-gray-950 border border-gray-800 rounded-xl p-4 overflow-auto">
        <div className="text-sm font-semibold mb-2">Top Stock Ignition Setups</div>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-gray-400 border-b border-gray-800"><th className="py-2">Ticker</th><th>Side</th><th>Score</th><th>Setup</th><th>5m RVOL</th><th>15m Move</th><th>Extension</th><th>Decision</th></tr></thead>
          <tbody>{candidates.map((r, i) => <tr key={`${r.ticker}-${i}`} className="border-b border-gray-900"><td className="py-2 font-semibold">{r.ticker}</td><td className={r.direction === "CALL" ? "text-green-300" : "text-red-300"}>{r.direction}</td><td>{Number(r.score || r.stage1_score || 0).toFixed(1)}</td><td>{r.setup_type || "-"}</td><td>{Number(r.volume_ratio_5m || 0).toFixed(2)}x</td><td>{Number(r.change_15m_pct || 0).toFixed(2)}%</td><td>{Number(r.extension_atr || 0).toFixed(2)} ATR</td><td className={r.qualified ? "text-green-300" : "text-gray-400"}>{r.qualified ? "READY" : (r.reason || "WAIT")}</td></tr>)}
          {!candidates.length ? <tr><td colSpan={8} className="py-3 text-gray-500">No ignition scan yet.</td></tr> : null}</tbody>
        </table>
      </div>

      <div className="bg-gray-950 border border-gray-800 rounded-xl p-4 overflow-auto"><div className="text-sm font-semibold mb-2">Open Ignition Option Trades</div><pre className="text-xs text-gray-300 whitespace-pre-wrap">{JSON.stringify(open, null, 2)}</pre></div>

      <div className="bg-gray-950 border border-gray-800 rounded-xl overflow-hidden">
        <div className="p-3 text-sm font-semibold border-b border-gray-800">Stock Ignition Logs</div>
        <div className="max-h-72 overflow-auto"><table className="w-full text-xs"><thead className="bg-gray-900"><tr><th className="p-2 text-left">Time</th><th className="p-2 text-left">Level</th><th className="p-2 text-left">Symbol</th><th className="p-2 text-left">Event</th></tr></thead><tbody>{logs.map((l) => <tr key={l.id} className="border-t border-gray-900"><td className="p-2">{l.ts}</td><td className="p-2">{l.level}</td><td className="p-2">{l.symbol || "-"}</td><td className="p-2 text-gray-400">{l.event}</td></tr>)}</tbody></table></div>
      </div>
    </div>
  );
}
